import DateFnsUtils from '@date-io/date-fns'
import { yupResolver } from '@hookform/resolvers'
import { UploadPreview } from '@ifca-root/react-component/src/components/Avatar/UploadPreview'
import CardContents from '@ifca-root/react-component/src/components/CardList/CardContents'
import MainHeader from '@ifca-root/react-component/src/components/Header/MainHeader'
import { FileUploadInput } from '@ifca-root/react-component/src/components/Input/FileUploadInput'
import { ContentWrapper } from '@ifca-root/react-component/src/components/Layout/ContentWrapper'
import Loading from '@ifca-root/react-component/src/components/Loading/Loading'
import useUploadAttachment from '@ifca-root/react-component/src/utils/hooks/useUploadAttachment'
import {
  Grid,
  ListItemText,
  ListSubheader,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core'
import { Autocomplete } from '@material-ui/lab'
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider,
} from '@material-ui/pickers'
import { AccountClient } from 'AccountClient'
import { ErrorDialog } from 'components/Dialog/ErrorDialog'
import { ExitConfirmationDialog } from 'components/Dialog/ExitConfirmationDialog'
import { AccountFooter } from 'components/Footer/AccountFooter'
import { TooltipProforma } from 'components/Footer/TooltipProforma'
import SnackBarContext from 'containers/App/Store/SnackBarContext'
import { calculateDocAmt } from 'containers/ARAPModule/Helper/AmountCalculation'
import {
  ApprovalStatus,
  useCreateProformaRequisitionMutation,
  useDocumentListingLazyQuery,
  useGetBillItemByScheduledBillingLazyQuery,
  useGetDebtorAccountQuery,
  useGetDepartmentListingByItemIdLazyQuery,
  useGetScheduledBillingBySalesOrderDebtorLazyQuery,
  useGetTaxSchemeNewListingQuery,
  useGetUnusedSalesOrderProformaByDebtorLazyQuery,
  useUpdateProformaInvoiceMutation,
} from 'generated/graphql'
import { calculateTaxDetail } from 'helpers/Calculation/taxCalculationDetail'
import { filterTaxSchemeEffectiveDate } from 'helpers/Filter/filterTaxSchemeEffectiveDate'
import { useMenuOption } from 'helpers/Hooks/useMenuOption'
import { SystemMsgs } from 'helpers/Messages/SystemMsg'
import { formatDashDate } from 'helpers/StringNumberFunction/FormatDate'
import { amtStr } from 'helpers/StringNumberFunction/NumFormatters'
import { CommonYupValidation } from 'helpers/YupSchema/yup'
import React, { Fragment, useContext, useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import NumberFormat from 'react-number-format'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import { uuid } from 'uuidv4'
import * as yup from 'yup'

interface ProformaInvoicesRequisitionFormProps {
  CompanyID: string
  DebtorAccountID: string
  SalesOrderID: string
  ScheduledBillingID: string
  ScheduledBillingItemID: string
  BillItemID: string
  CostCentreID: string
  InvoiceDate: Date
  TransactionDate: Date
  RefNo: string
  Amount: number
  TaxSchemeID: string
  TaxAmt: number
  Description: string
  Remark: string
  TaxAmtDetail: any
}

export const ProformaInvoicesForm = (props: any) => {
  let history = useHistory()
  let location = useLocation()
  const editData: any = location?.state
  const { formMode, formLabel } = props
  const { CompanyID }: any = useParams()
  const user = JSON.parse(localStorage.getItem('loggedInUser'))
  const { ProformaRequisitionID }: any = useParams()
  const { menu } = useMenuOption()

  /* ------------------------------------------- */
  /*                   STATE                     */
  /* ------------------------------------------- */
  const [mountDefVal, setMountDefVal] = useState(!!editData)
  const [openExitConf, setOpenExitConf] = useState(null)
  const { setOpenSnackBar, setSnackBarMsg }: any = useContext(
    SnackBarContext
  ) as any
  const [errMessage, setErrMessage] = useState(null)
  const [errDialog, setErrDialog] = useState(false)
  const [initDocs, setInitDocs] = useState([])
  const [billItemID, setBillItemID] = useState(editData?.BillItemID ?? null)
  const [isErrorCall, setIsErrorCall] = useState<boolean>(false)

  const [selectedTax, setSelectedTax] = useState(null)
  const [taxAmt, setTaxAmt] = useState(
    !!editData?.TaxAmt ? editData?.TaxAmt : 0
  )
  const [billingAmt, setBillingAmt] = useState(0)
  const [allowValueChange, setAllowValueChange] = useState<boolean>(!editData)

  /* --------------------------------------------------- */
  /*                ADHOC BILLING CONTROL                */
  /* --------------------------------------------------- */
  const [isAdhocBilling, setIsAdhocBilling] = useState<boolean>(
    !editData?.ScheduledBillingItemID && !editData?.ScheduledBillingID
  )
  const [showScheduledBilling, setShowScheduledBilling] = useState(true)
  /* -------------------------------------------- */
  /*                   USE FORM                   */
  /* -------------------------------------------- */
  const ProformaInvoiceRequisitionSchema = yup.object().shape({
    DebtorAccountID: CommonYupValidation.requireField(SystemMsgs.debtorName()),
    SalesOrderID: CommonYupValidation.requireField(SystemMsgs.salesOrder()),
    CostCentreID: CommonYupValidation.requireField(SystemMsgs.department()),
    InvoiceDate: yup.string().required('Doc Date is Required'),
    TransactionDate: yup.string().required('Transaction Date is Required'),
    TaxSchemeID: CommonYupValidation.requireField(SystemMsgs.tax()),
    Amount: yup
      .string()
      .notOneOf([0, '0', '0.00', '-0', '-0.00'], 'Amount cannot be 0')
      .required('Amount is required'),
    RefNo: CommonYupValidation.requireField(SystemMsgs.referenceNo()),
    Description: CommonYupValidation.requireField(SystemMsgs.description()),
    Remark: CommonYupValidation.requireField(SystemMsgs.remark()),
  })

  const {
    handleSubmit,
    register,
    setValue,
    control,
    errors,
    watch,
    clearErrors,
  } = useForm<ProformaInvoicesRequisitionFormProps>({
    defaultValues: {
      DebtorAccountID: editData?.DebtorAccountID
        ? editData?.DebtorAccountID
        : null,
      SalesOrderID: editData?.SalesOrderID ? editData?.SalesOrderID : null,
      ScheduledBillingID: editData?.ScheduledBillingID
        ? editData?.ScheduledBillingID
        : null,
      ScheduledBillingItemID: editData?.ScheduledBillingItemID
        ? editData?.ScheduledBillingItemID
        : null,
      CostCentreID: editData?.CostCentreID ? editData?.CostCentreID : null,
      InvoiceDate: editData?.InvoiceDate ? editData?.InvoiceDate : new Date(),
      TransactionDate: editData?.TransactionDate
        ? editData?.TransactionDate
        : new Date(),
      Amount: editData?.Amount ? editData?.Amount : 0,
      RefNo: editData?.RefNo ? editData?.RefNo : '',
      Description: editData?.Description ? editData?.Description : '',
      Remark: editData?.Remark ? editData?.Remark : '',
    },
    mode: 'onSubmit',
    resolver: yupResolver(ProformaInvoiceRequisitionSchema),
  })

  /* ------------------------------------------- */
  /*                   QUERY                     */
  /* ------------------------------------------- */
  const {
    loading: DebtorAccountLoading,
    data: { getDebtorAccount } = { getDebtorAccount: [] },
  } = useGetDebtorAccountQuery({
    fetchPolicy: 'network-only',
    variables: { CompanyID },
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
  })

  // const [
  //   fetchSalesOrder,
  //   {
  //     loading: SalesOrderLoading,
  //     data: { getSalesOrderByDebtor } = { getSalesOrderByDebtor: [] },
  //   },
  // ] = useGetSalesOrderByDebtorLazyQuery({
  //   fetchPolicy: 'network-only',
  //   onError: ({ message }) => {
  //     let error = message?.substring(15)
  //     setErrMessage(error)
  //     setErrDialog(true)
  //   },
  // })

  const [
    fetchSalesOrder,
    {
      loading: SalesOrderLoading,
      data: { getUnusedSalesOrderProformaByDebtor } = {
        getUnusedSalesOrderProformaByDebtor: [],
      },
    },
  ] = useGetUnusedSalesOrderProformaByDebtorLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
  })
  console.log('watch: ', watch())

  const [
    fetchScheduledBill,
    {
      loading: ScheduledBillLoading,
      data: { getScheduledBillingBySalesOrderDebtor } = {
        getScheduledBillingBySalesOrderDebtor: [],
      },
    },
  ] = useGetScheduledBillingBySalesOrderDebtorLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
  })

  const [
    fetchScheduleBillItem,
    {
      loading: ScheduledBillItemLoading,
      data: { getBillItemByScheduledBilling } = {
        getBillItemByScheduledBilling: [],
      },
    },
  ] = useGetBillItemByScheduledBillingLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: ({ getBillItemByScheduledBilling }) => {
      if (getBillItemByScheduledBilling?.length > 0) {
        const filteredBillItems = getBillItemByScheduledBilling.filter(
          item =>
            item.ScheduledBillingItemID === editData?.ScheduledBillingItemID ||
            !(item.ProformaRequisitionID && item.RecordStatus === 'ACTIVE')
        )

        register('ScheduledBillingItemID')
        setValue(
          'ScheduledBillingItemID',
          !!mountDefVal
            ? editData?.ScheduledBillingItemID
            : watch('ScheduledBillingID')
            ? filteredBillItems[0]?.ScheduledBillingItemID
            : null
        )

        fetchDepartment({
          variables: {
            CompanyID,
            BillItemID: !!mountDefVal
              ? editData?.BillItemID
              : watch('ScheduledBillingID')
              ? filteredBillItems[0]?.BillItemID
              : null,
          },
        })
        register('Description')
        setValue(
          'Description',
          !!mountDefVal
            ? editData?.Description
            : filteredBillItems[0]?.BillingDescription ?? null
        )
        register('Remark')
        setValue(
          'Remark',
          !!mountDefVal
            ? editData?.Description
            : filteredBillItems[0]?.BillingDescription ?? null
        )
        register('TaxSchemeID')
        setValue(
          'TaxSchemeID',
          !!mountDefVal
            ? editData?.TaxSchemeID
            : filteredBillItems[0]?.TaxSchemeID ?? null
        )

        setSelectedTax(
          taxSchemeOutput?.find(
            x =>
              x?.id ===
              (mountDefVal
                ? editData?.TaxSchemeID
                : filteredBillItems[0]?.TaxSchemeID ?? null)
          )
        )
        setBillingAmt(
          !!mountDefVal ? billingAmt : filteredBillItems[0]?.BillingAmt ?? 0
        )

        register('Amount')
        setValue(
          'Amount',
          !!mountDefVal ? billingAmt : filteredBillItems[0]?.BillingAmt ?? 0
        )

        setBillItemID(
          !!mountDefVal
            ? editData?.BillItemID
            : filteredBillItems[0]?.BillItemID ?? null
        )
      }
    },
  })

  const filteredBillItems = getBillItemByScheduledBilling.filter(
    item =>
      item.ScheduledBillingItemID === editData?.ScheduledBillingItemID ||
      !(item.ProformaRequisitionID && item.RecordStatus === 'ACTIVE')
  )

  const [
    fetchDepartment,
    {
      loading: DepartmentLoading,
      data: { getDepartmentListingByItemID } = {
        getDepartmentListingByItemID: [],
      },
    },
  ] = useGetDepartmentListingByItemIdLazyQuery({
    fetchPolicy: 'network-only',
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: ({ getDepartmentListingByItemID }) => {
      if (
        getDepartmentListingByItemID?.length === 1 &&
        !!getDepartmentListingByItemID[0]?.IsDefault
      ) {
        setValue('CostCentreID', getDepartmentListingByItemID[0]?.CostCentreID)
      }
      if (!!editData?.CostCentreID && mountDefVal === true) {
        setValue('CostCentreID', editData?.CostCentreID)
      }
    },
  })

  const disableDepartmentController: () => boolean = () => {
    return (
      (watch('ScheduledBillingID') !== null &&
        watch('ScheduledBillingID') !== undefined &&
        watch('ScheduledBillingID') !== '') ||
      !!getDepartmentListingByItemID[0]?.IsDefault
    )
  }

  const {
    loading: TaxSchemeLoading,
    data: { getTaxSchemeListing } = { getTaxSchemeListing: [] },
  } = useGetTaxSchemeNewListingQuery({
    fetchPolicy: 'network-only',
    client: AccountClient,
    variables: { TaxClass: 'OUTPUT' },
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: () => {
      if (editData?.TaxSchemeID) {
        let filteredTaxScheme = filterTaxSchemeEffectiveDate(
          editData?.BillingDate,
          getTaxSchemeListing
        )

        let selectTax = filteredTaxScheme.find(
          x => x?.id === editData?.TaxSchemeID
        )
        setSelectedTax(selectTax)
        setValue('TaxSchemeID', selectTax?.id)
        clearErrors('TaxSchemeID')

        initializeTaxDetailsInEditMode(selectTax)

        let recalculateCost =
          selectTax?.isExclusive === true
            ? editData?.DocAmt - editData?.TaxAmt
            : editData?.DocAmt

        setBillingAmt(recalculateCost)
        setValue('Amount', recalculateCost)
      }

      if (mountDefVal) {
        fetchScheduledBill({
          variables: {
            CompanyID,
            DebtorAccountID: watch('DebtorAccountID'),
            SalesOrderID: editData?.SalesOrderID,
          },
        })
        setValue('ScheduledBillingID', editData?.ScheduledBillingID)
        fetchSalesOrder({
          variables: {
            DebtorAccountID: editData?.DebtorAccountID,
            excludeSalesOrderID: editData?.SalesOrderID ?? null,
          },
        })
        fetchScheduleBillItem({
          variables: {
            CompanyID,
            ScheduledBillingID: editData?.ScheduledBillingID,
            isBilled: true,
          },
        })
        fetchDepartment({
          variables: {
            CompanyID,
            BillItemID: editData?.BillItemID,
          },
        })
      } else {
        fetchScheduleBillItem({ variables: { CompanyID } })
      }

      // fetchScheduledBill({ variables: { CompanyID } })
      setValue('CostCentreID', editData?.CostCentreID ?? null)
    },
  })

  const taxSchemeOutput = filterTaxSchemeEffectiveDate(
    watch('BillingDate'),
    getTaxSchemeListing
  )

  const [
    fetchDocuments,
    { loading: DocumentLoading, data: DocData },
  ] = useDocumentListingLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: ({ DocumentListing }) => {
      if (!editData?.files) {
        handleEditUpload(
          DocumentListing?.filter(doc => doc?.description !== 'document_pdf')
        )
        previewFiles?.push(
          ...DocumentListing?.filter(
            doc => doc?.description !== 'document_pdf'
          )?.map(x => x?.fileURL)
        )
      }
    },
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
  })

  /* ------------------------------------------- */
  /*                   MUTATION                  */
  /* ------------------------------------------- */
  const [
    createProformaRequisition,
    { loading: CreateProformaRequisitionLoading },
  ] = useCreateProformaRequisitionMutation({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: data => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.createNewRecord())
      setTimeout(() => {
        history.push({
          pathname: `/account-receivable/${CompanyID}/proforma-invoices/proforma-invoice-requisition`,
          state: { success: true, msgMode: 'create' },
        })
      }, 500)
    },
  })

  const [
    updateProformaRequisition,
    { loading: UpdateProformaRequisitionLoading },
  ] = useUpdateProformaInvoiceMutation({
    onError: ({ message }) => {
      let error = message?.substring(15)
      setErrMessage(error)
      setErrDialog(true)
    },
    onCompleted: data => {
      setOpenSnackBar(true)
      setSnackBarMsg(SystemMsgs.updateRecord())
      setTimeout(() => {
        history.push({
          pathname: `/account-receivable/${CompanyID}/proforma-invoices/proforma-invoice-requisition`,
          state: { success: true, msgMode: 'update' },
        })
      }, 500)
    },
  })

  /* -------------------------------------------- */
  /*                  ATTACHMENT                  */
  /* -------------------------------------------- */
  const {
    files,
    setFiles,
    setPreviewFiles,
    handleUploadChange,
    previewFiles,
    remove: removeFile,
    handleEditUpload,
  } = useUploadAttachment()

  useEffect(() => {
    if (!DocumentLoading && formMode !== 'add') {
      fetchDocuments({ variables: { refID: editData?.ProformaRequisitionID } })
    }

    if (editData?.files) {
      setFiles(editData?.files)
      setPreviewFiles(
        editData?.files?.map(file => {
          return URL.createObjectURL(file)
        })
      )
    }
  }, [formMode, editData?.InvoiceID, editData])

  useEffect(() => {
    if (editData?.Attachment) {
      setInitDocs(editData?.Attachment)
    }
  }, [editData])
  console.log('ddd: ', editData)

  /* -------------------------------------------- */
  /*                    FOOTER                    */
  /* -------------------------------------------- */
  const disableDraft = !watch('DebtorAccountID') || !watch('SalesOrderID')

  const disableSubmit =
    disableDraft ||
    !watch('InvoiceDate') ||
    !watch('TransactionDate') ||
    !watch('Description') ||
    !watch('Remark') ||
    !watch(isAdhocBilling ? 'BillItemID' : 'ScheduledBillingItemID')

  const draftFooterOption = {
    name: 'Save as Draft',
    onClick: () => {
      setIsErrorCall(true)
      handleSubmit(data => onSubmit(data, 'draft'))()
    },
    color: 'primary',
    props: { type: 'submit' },
    disabled: disableDraft,
  }

  const submitFooterOption = {
    name: 'Submit',
    onClick: () => {
      setIsErrorCall(true)
      handleSubmit(data => onSubmit(data, 'submit'))()
    },
    color: 'primary',
    props: { type: 'submit' },
    disabled: disableSubmit,
  }

  let footerOptions: any[] = [draftFooterOption, submitFooterOption]

  const handleAmtChange = event => {
    setBillingAmt(event.value)
    setValue('Amount', event?.value)
  }

  /* -------------------------------------------- */
  /*                   ON SUBMIT                  */
  /* -------------------------------------------- */
  const createUpdateProformaRequisition = ProformaRequisitionID
    ? updateProformaRequisition
    : createProformaRequisition

  const onSubmit = (data, status) => {
    const formattedTaxDetails = result.taxDetails.map((tax, idx) => {
      return {
        tax_amt: Number(watch(`TaxAmtDetail[${idx}]`)) ?? tax.TaxAmount,
        tax_rate: tax.TaxRate * 100,
        tax_id: tax.taxId,
        tax_scheme_detail_id: tax.taxSchemeDetailId,
        tax_scheme_id: tax.taxSchemeId,
      }
    })

    createUpdateProformaRequisition({
      variables: {
        ...(ProformaRequisitionID && { ProformaRequisitionID }),
        input: {
          CompanyID,
          DebtorAccountID: data?.DebtorAccountID,
          SalesOrderID: data?.SalesOrderID,
          BillItemID: billItemID,
          ...(!isAdhocBilling && {
            ScheduledBillingItemID: data?.ScheduledBillingItemID,
          }),
          CostCentreID: data?.CostCentreID,
          InvoiceDate: formatDashDate(
            new Date(data?.InvoiceDate).toISOString()
          ),
          TransactionDate: formatDashDate(
            new Date(data?.TransactionDate).toISOString()
          ),
          Amount: Number(docAmt) - Number(taxAmt),
          TaxAmt: Number(taxAmt),
          TaxRate: 0,
          DocAmt: Number(docAmt),
          TaxSchemeID: data?.TaxSchemeID,
          RefNo: data?.RefNo,
          Description: data?.Description,
          Remark: data?.Remark,
          ApprovalStatus:
            status === 'draft' ? ApprovalStatus.Active : ApprovalStatus.Submit,
          taxInput: formattedTaxDetails,
        },
      },
    })
  }

  // ✥✥✥✥✥✥✥✥✥✥✥✥✥✥ - TAX CALCULATION - ✥✥✥✥✥✥✥✥✥✥✥✥✥✥ //
  const prepareTaxCalculationData = (selectedTax: any) => {
    if (!selectedTax?.taxSchemeDetailList[0]?.taxSchemeDetailItemList?.length) {
      return []
    }

    const taxSchemeDetail = selectedTax?.taxSchemeDetailList[0]

    return taxSchemeDetail.taxSchemeDetailItemList.map(item => ({
      taxId: item.taxId,
      taxSchemeId: taxSchemeDetail.taxSchemeId,
      taxSchemeDetailId: taxSchemeDetail.id,
      seqNo: item.seqNo,
      taxOnSeq: item.taxOnSeq || [],
      computeMethod:
        item.computeMethod === 'TAX_ON_GROSS'
          ? 'Tax_On_Gross'
          : 'Tax_On_Taxed_Price',
      taxRate: item.taxRate,
      taxCode: item.taxInfo?.code,
    }))
  }

  const calculateTaxFromState = (selectedTax: any, amount: number) => {
    const taxData = prepareTaxCalculationData(selectedTax)

    const isExclusive = Boolean(selectedTax?.isExclusive)

    return calculateTaxDetail(taxData, amount, isExclusive)
  }
  let result = calculateTaxFromState(selectedTax, billingAmt)

  const initializeTaxDetailsInEditMode = selectTax => {
    if (editData?.TaxAmtDetails && Array.isArray(editData?.TaxAmtDetails)) {
      menu.obj.TaxAmtDetails.forEach((taxDetail, index) => {
        register(`TaxAmtDetail[${index}]`)
        setValue(`TaxAmtDetail[${index}]`, taxDetail.taxAmount)
      })
      setValue('taxDetailsCount', menu.obj.TaxAmtDetails.length)
    } else if (selectTax) {
      const currentAmount = Number(
        selectTax?.isExclusive === true ? editData?.Amount : editData?.DocAmt
      )
      const taxResult = calculateTaxFromState(selectTax, currentAmount)
      if (taxResult?.taxDetails) {
        updateTaxDetailsInForm(taxResult.taxDetails, taxResult.TaxAmount)
      }
    }
  }

  const updateTaxDetailsInForm = (taxDetails, taxAmount) => {
    if (!taxDetails || taxDetails.length === 0) return
    const prevTaxDetailsCount = watch('taxDetailsCount') || 0
    taxDetails.forEach((tax, index) => {
      register(`TaxAmtDetail[${index}]`)
      setValue(`TaxAmtDetail[${index}]`, tax.TaxAmount)
    })
    setValue('taxDetailsCount', taxDetails.length)
    if (prevTaxDetailsCount > taxDetails.length) {
      for (let i = taxDetails.length; i < prevTaxDetailsCount; i++) {
        setValue(`TaxAmtDetail[${i}]`, null)
      }
    }
    setValue('TaxAmt', taxAmount)
    setTaxAmt(taxAmount)
  }

  const docAmt = selectedTax?.isExclusive
    ? calculateDocAmt(billingAmt, taxAmt)
    : Number(billingAmt)

  const handleTaxChange = e => {
    const currentTax = taxSchemeOutput.find(x => x?.id === e)
    const taxResult = calculateTaxFromState(currentTax, billingAmt)
    updateTaxDetailsInForm(taxResult?.taxDetails, taxResult?.TaxAmount)
    setSelectedTax(taxSchemeOutput?.find(x => x?.id === e))
    setTaxAmt(taxResult?.TaxAmount)
  }
  const salesOrderID = editData?.SalesOrderID || watch('SalesOrderID')
  const selectedOrder = getUnusedSalesOrderProformaByDebtor?.find(
    order => order?.id === salesOrderID
  )
  return (
    <>
      {TaxSchemeLoading && <Loading />}
      {DepartmentLoading && <Loading />}
      {SalesOrderLoading && <Loading />}
      {DebtorAccountLoading && <Loading />}
      {ScheduledBillLoading && <Loading />}
      {ScheduledBillItemLoading && <Loading />}
      {CreateProformaRequisitionLoading && <Loading />}
      {UpdateProformaRequisitionLoading && <Loading />}
      <MainHeader
        mainBtn="close"
        onClick={() => {
          history.push(
            `/account-receivable/${CompanyID}/proforma-invoices/proforma-invoice-requisition`
          )
        }}
        smTitle={'Account Receivable'}
        title={user?.companyName}
        routeSegments={[
          { name: 'Main Menu' },
          { name: 'Account Receivable' },
          { name: 'Proforma Invoice' },
          { name: 'Proforma Invoice Requisition', current: true },
        ]}
        rightRouteSegments={[{ name: formLabel, current: true }]}
      />

      <ContentWrapper float footer>
        <CardContents>
          {!DebtorAccountLoading && (
            <Controller
              render={({ value, onChange }) => {
                const defVal = !!watch('DebtorAccountID')
                  ? getDebtorAccount?.find(
                      coa => coa?.DebtorAccountID === watch('DebtorAccountID')
                    )
                  : null
                return (
                  <Autocomplete
                    key={uuid()}
                    ref={register}
                    options={
                      getDebtorAccount?.sort((a, b) => {
                        return a?.DebtorName?.localeCompare(b?.DebtorName)
                      }) || []
                    }
                    getOptionLabel={option => {
                      return `${option?.DebtorName}`
                    }}
                    fullWidth
                    // disabled={!isAdhocBilling}
                    onChange={(value, newValue: any, reason) => {
                      setValue('DebtorAccountID', newValue?.DebtorAccountID)

                      if (reason === 'clear') {
                        // Clear all form values
                        setValue('DebtorAccountID', '')
                        setValue('SalesOrderID', '')
                        setValue('ScheduledBillingID', '')
                        setBillItemID('')
                        setValue('ScheduledBillingItemID', '')
                        setValue('CostCentreID', '')
                        setValue('TaxSchemeID', '')
                        setValue('Remark', '')
                        setValue('RefNo', '')
                        setValue('Description', '')
                        setValue('TaxAmt', 0)
                        setValue('Amount', 0)

                        // ✅ ADD THIS: Hide scheduled billing options
                        setShowScheduledBilling(false)
                      } else {
                        setValue('SalesOrderID', '')
                        setValue('ScheduledBillingID', '')
                        setBillItemID('')
                        setValue('ScheduledBillingItemID', '')
                        setValue('CostCentreID', '')
                        setValue('TaxSchemeID', '')
                        setValue('Remark', '')
                        setValue('RefNo', '')
                        setValue('Description', '')
                        setValue('TaxAmt', 0)
                        setValue('Amount', 0)
                        // When debtor is selected, fetch data and show options
                        fetchSalesOrder({
                          variables: {
                            DebtorAccountID: newValue?.DebtorAccountID ?? null,
                            excludeSalesOrderID: editData?.SalesOrderID ?? null,
                          },
                        })
                        fetchScheduledBill({
                          variables: {
                            CompanyID,
                            DebtorAccountID: newValue?.DebtorAccountID,
                            SalesOrderID: editData?.SalesOrderID,
                          },
                        })

                        // ✅ ADD THIS: Show scheduled billing options when debtor is selected
                        setShowScheduledBilling(true)
                      }

                      setMountDefVal(false)
                    }}
                    defaultValue={defVal}
                    renderOption={(props, option) => {
                      return (
                        <div>
                          <div>
                            <span className="xsTitle">{props?.DebtorName}</span>
                          </div>
                        </div>
                      )
                    }}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            helperText={errors?.DebtorAccountID?.message}
                            error={errors?.DebtorAccountID ? true : false}
                            label="Debtor Name"
                            style={{ width: '100%' }}
                            required
                            margin="normal"
                            defaultValue={defVal}
                            // disabled={!isAdhocBilling}
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              label="Debtor Name"
              name="DebtorAccountID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="normal"
              ref={register}
              required
              helperText={errors?.DebtorAccountID?.message}
              error={errors?.DebtorAccountID ? true : false}
              defaultValue={mountDefVal ? editData?.DebtorAccountID : ''}
              // disabled={!isAdhocBilling}
            />
          )}

          <Controller
            render={({ value, onChange }) => {
              const val = watch('ScheduledBillingID')
                ? getScheduledBillingBySalesOrderDebtor?.filter(
                    x => x?.ScheduledBillingID === watch('ScheduledBillingID')
                  )[0]
                : null

              const defVal = watch('ScheduledBillingID')
                ? getScheduledBillingBySalesOrderDebtor?.filter(
                    x => x?.ScheduledBillingID === editData?.ScheduledBillingID
                  )[0]
                : null
              return (
                <Autocomplete
                  key={uuid()}
                  options={
                    showScheduledBilling
                      ? getScheduledBillingBySalesOrderDebtor || []
                      : []
                  }
                  getOptionLabel={option => {
                    return `${option?.title}`
                  }}
                  fullWidth
                  onChange={(value, newValue: any, reason) => {
                    setValue('ScheduledBillingID', newValue?.ScheduledBillingID)
                    setIsAdhocBilling(false)
                    setMountDefVal(false)
                    fetchSalesOrder({
                      variables: {
                        DebtorAccountID: newValue?.DebtorAccountID ?? null,
                        excludeSalesOrderID: newValue?.SalesOrderID ?? null,
                      },
                    })

                    // setValue('DebtorAccountID', newValue?.DebtorAccountID)
                    setValue('SalesOrderID', newValue?.SalesOrderID)
                    setValue('CostCentreID', newValue?.CostCentreID)
                    setValue('RefNo', newValue?.title)
                    register('Remark')
                    // setValue('Remark', newValue?.title)

                    if (reason === 'clear') {
                      setValue('ScheduledBillingID', '')
                      // setValue('DebtorAccountID', '')
                      setValue('SalesOrderID', '')
                      setValue('CostCentreID', '')
                      setValue('ScheduledBillingItemID', '')
                      setValue('RefNo', '')
                      setBillItemID('')
                      setIsAdhocBilling(true)
                    }

                    register('Amount')
                    setValue('Amount', newValue?.BillingAmt)
                    setBillingAmt(newValue?.BillingAmt)
                    fetchScheduleBillItem({
                      variables: {
                        CompanyID,
                        ScheduledBillingID: newValue?.ScheduledBillingID,
                        isBilled: true,
                      },
                    })
                  }}
                  value={val}
                  defaultValue={defVal ?? null}
                  renderOption={(props, option) => {
                    return (
                      <div>
                        <div>
                          <span className="xsTitle">{props?.title}</span>
                        </div>
                      </div>
                    )
                  }}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          label="Schedule Billing"
                          style={{ width: '100%' }}
                          margin="normal"
                          defaultValue={defVal ?? null}
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            label="Schedule Billing"
            name="ScheduledBillingID"
            autoComplete="off"
            control={control}
            multiline={true}
            fullWidth
            margin="normal"
            ref={register}
            defaultValue={
              mountDefVal && editData?.ScheduledBillingID
                ? editData?.ScheduledBillingID
                : ''
            }
          />

          <Controller
            render={({ value, onChange }) => {
              const defVal = getUnusedSalesOrderProformaByDebtor?.find(
                coa => coa?.id === watch('SalesOrderID')
              )
              return (
                <Autocomplete
                  key={uuid()}
                  options={getUnusedSalesOrderProformaByDebtor || []}
                  getOptionLabel={option => {
                    const revised = option?.revisedOrderSum
                    const fallback = option?.orderSum
                    const amount = revised ? amtStr(revised) : amtStr(fallback)
                    return `${option?.title || 'N/A'} | ${amount || 'N/A'}`
                  }}
                  fullWidth
                  disabled={!isAdhocBilling}
                  onChange={(value, newValue: any, reason) => {
                    setMountDefVal(false) // always return to false

                    if (reason === 'clear') {
                      setValue('SalesOrderID', '')
                      setValue('ScheduledBillingID', '')
                      setValue('ScheduledBillingItemID', '')
                      setBillItemID('')
                      setValue('CostCentreID', '')
                      setValue('TaxSchemeID', '')
                      setValue('TaxAmt', 0)
                      setValue('Amount', 0)
                    } else {
                      setValue('SalesOrderID', newValue?.id)
                      if (
                        watch('DebtorAccountID') &&
                        !watch('ScheduledBillingID')
                      ) {
                        setIsAdhocBilling(true)
                      }
                    }

                    fetchScheduledBill({
                      variables: {
                        CompanyID,
                        DebtorAccountID: watch('DebtorAccountID'),
                        SalesOrderID: newValue?.id,
                      },
                    })
                  }}
                  value={defVal}
                  defaultValue={defVal}
                  renderOption={(props, option) => (
                    <Fragment>
                      <ListItemText
                        primary={
                          <Grid container>
                            <Grid
                              item
                              xs={8}
                              component="span"
                              className="xsTitle "
                            >
                              {props?.title || 'N/A'}
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              component="span"
                              className="xsTitle right-text highlight-text"
                            >
                              {amtStr(
                                props?.revisedOrderSum ?? props?.orderSum
                              )}
                            </Grid>
                          </Grid>
                        }
                      />
                    </Fragment>
                  )}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          helperText={errors?.SalesOrderID?.message}
                          error={errors?.SalesOrderID ? true : false}
                          label="Sales Order"
                          style={{ width: '100%' }}
                          required
                          margin="normal"
                          disabled={!isAdhocBilling}
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            label="Sales Order"
            name="SalesOrderID"
            autoComplete="off"
            control={control}
            multiline={true}
            fullWidth
            margin="normal"
            ref={register}
            required
            helperText={errors?.SalesOrderID?.message}
            error={errors?.SalesOrderID ? true : false}
            defaultValue={mountDefVal ? editData?.SalesOrderID : ''}
            disabled={!isAdhocBilling}
          />

          {!isAdhocBilling ? (
            <Controller
              render={({ onChange, onBlur, value }) => {
                const defVal = !!watch('ScheduledBillingItemID')
                  ? filteredBillItems?.find(
                      x =>
                        x?.ScheduledBillingItemID ===
                        watch('ScheduledBillingItemID')
                    )
                  : null

                return (
                  <Autocomplete
                    key={uuid()}
                    options={
                      watch('ScheduledBillingID')
                        ? filteredBillItems?.sort((a, b) => {
                            return a.Name.localeCompare(b.Name)
                          })
                        : []
                    }
                    getOptionLabel={option =>
                      `${option?.Name} | ${amtStr(option?.BillingAmt)} | ${
                        option?.BillingDescription
                      }`
                    }
                    fullWidth
                    onChange={(value, newValue: any, reason) => {
                      setValue(
                        'ScheduledBillingItemID',
                        newValue?.ScheduledBillingItemID
                      )
                      setBillingAmt(newValue?.BillingAmt)
                      setValue('Amount', newValue?.BillingAmt)
                      register('Description')
                      setValue('Description', newValue?.BillingDescription)
                      register('Remark')
                      setValue('Remark', newValue?.BillingDescription)
                      setMountDefVal(false)
                      if (newValue?.TaxSchemeID) {
                        register('TaxSchemeID')
                        setValue('TaxSchemeID', newValue?.TaxSchemeID)
                        setSelectedTax(
                          taxSchemeOutput?.find(
                            x => x?.id === newValue?.TaxSchemeID
                          )
                        )
                      }
                      setBillItemID(newValue?.BillItemID)

                      if (reason === 'clear') {
                        setValue('ScheduledBillingItemID', '')
                      }
                    }}
                    renderOption={(props, option) => (
                      <>
                        <Fragment>
                          <ListItemText
                            primary={
                              <>
                                <Grid container spacing={1}>
                                  <Grid item xs={12}>
                                    <Grid container>
                                      <Grid
                                        item
                                        xs={8}
                                        component="span"
                                        className="xsTitle"
                                      >
                                        {props?.Name || 'N/A'}
                                      </Grid>
                                      <Grid
                                        item
                                        xs={4}
                                        component="span"
                                        className="xsTitle right-text highlight-text"
                                      >
                                        {amtStr(props?.BillingAmt)}
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                  <Grid item xs={12}>
                                    <Grid container>
                                      <Grid
                                        item
                                        xs={12}
                                        component="span"
                                        className="desc left-text"
                                      >
                                        {props?.BillingDescription || 'N/A'}
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </>
                            }
                          />
                        </Fragment>
                      </>
                    )}
                    value={defVal}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            required
                            label="Billing Item"
                            variant="standard"
                            fullWidth
                            name="ScheduledBillingItemID"
                            helperText={
                              isErrorCall === true &&
                              ['', null, undefined].includes(
                                watch('ScheduledBillingItemID')
                              )
                                ? SystemMsgs.billingItem()
                                : ''
                            }
                            error={
                              isErrorCall === true &&
                              ['', null, undefined].includes(
                                watch('ScheduledBillingItemID')
                              )
                                ? true
                                : false
                            }
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              name="ScheduledBillingItemID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="dense"
              ref={register}
              required
              helperText={
                isErrorCall === true &&
                ['', null, undefined].includes(watch('ScheduledBillingItemID'))
                  ? SystemMsgs.billingItem()
                  : ''
              }
              error={
                isErrorCall === true &&
                ['', null, undefined].includes(watch('ScheduledBillingItemID'))
                  ? true
                  : false
              }
              defaultValue={mountDefVal ? editData?.ScheduledBillingItemID : ''}
            />
          ) : (
            <Controller
              render={({ onChange, onBlur, value }) => {
                const defVal = filteredBillItems?.find(
                  x => x?.BillItemID === watch('BillItemID')
                )

                return (
                  <Autocomplete
                    key={uuid()}
                    options={
                      filteredBillItems?.sort((a, b) => {
                        return a.Name.localeCompare(b.Name)
                      }) || []
                    }
                    getOptionLabel={option =>
                      `${option?.Name} | ${option?.Description}`
                    }
                    fullWidth
                    onChange={(value, newValue: any, reason) => {
                      setValue('BillItemID', newValue?.BillItemID)
                      setMountDefVal(false)
                      if (newValue?.TaxSchemeID) {
                        register('TaxSchemeID')
                        setValue('TaxSchemeID', newValue?.TaxSchemeID)
                      }
                      setBillItemID(newValue?.BillItemID)
                      setValue('TaxSchemeID', newValue?.TaxSchemeID)

                      fetchDepartment({
                        variables: {
                          CompanyID,
                          BillItemID: newValue?.BillItemID,
                        },
                      })

                      if (reason === 'clear') {
                        setValue('BillItemID', '')
                      }
                    }}
                    renderOption={(props, option) => {
                      return (
                        <div>
                          <div>
                            <span className="xsTitle">{props?.Name}</span>
                          </div>
                          <div className="desc">{props?.Description}</div>
                        </div>
                      )
                    }}
                    value={defVal}
                    renderInput={(params: any) => {
                      return (
                        <div>
                          <TextField
                            {...params}
                            required
                            label="Billing Item"
                            variant="standard"
                            fullWidth
                            name="BillItemID"
                            helperText={
                              isErrorCall === true &&
                              ['', null, undefined].includes(
                                watch('BillItemID')
                              )
                                ? SystemMsgs.billingItem()
                                : ''
                            }
                            error={
                              isErrorCall === true &&
                              ['', null, undefined].includes(
                                watch('BillItemID')
                              )
                                ? true
                                : false
                            }
                          />
                        </div>
                      )
                    }}
                  />
                )
              }}
              name="BillItemID"
              autoComplete="off"
              control={control}
              multiline={true}
              fullWidth
              margin="dense"
              ref={register}
              required
              helperText={
                isErrorCall === true &&
                ['', null, undefined].includes(watch('BillItemID'))
                  ? SystemMsgs.billingItem()
                  : ''
              }
              error={
                isErrorCall === true &&
                ['', null, undefined].includes(watch('BillItemID'))
                  ? true
                  : false
              }
              defaultValue={mountDefVal ? editData?.BillItemID : ''}
            />
          )}

          <Controller
            render={({ value, onChange }) => {
              const defVal = getDepartmentListingByItemID?.find(
                x => x?.CostCentreID === watch('CostCentreID')
              )

              return (
                <Autocomplete
                  key={uuid()}
                  options={getDepartmentListingByItemID || []}
                  getOptionLabel={option =>
                    option ? `${option?.Code} | ${option?.Name}` : ''
                  }
                  fullWidth
                  onChange={(value, newValue: any) => {
                    onChange(newValue?.CostCentreID)
                    setValue('CostCentreID', newValue?.CostCentreID)
                  }}
                  disableClearable={disableDepartmentController()}
                  disabled={disableDepartmentController()}
                  value={
                    disableDepartmentController() === true
                      ? getDepartmentListingByItemID[0]
                      : defVal
                  }
                  renderOption={(props, option) => {
                    return (
                      <div>
                        <div>
                          <span className="xsTitle">{props?.Code}</span>
                        </div>
                        <div className="desc">{props?.Name}</div>
                      </div>
                    )
                  }}
                  renderInput={(params: any) => {
                    return (
                      <div>
                        <TextField
                          {...params}
                          helperText={errors?.CostCentreID?.message}
                          error={errors?.CostCentreID ? true : false}
                          label="Department"
                          style={{ width: '100%' }}
                          margin="dense"
                          required
                          disabled={disableDepartmentController()}
                        />
                      </div>
                    )
                  }}
                />
              )
            }}
            label="Department"
            name="CostCentreID"
            autoComplete="off"
            control={control}
            multiline={true}
            fullWidth
            margin="dense"
            ref={register}
            helperText={errors?.CostCentreID?.message}
            error={errors?.CostCentreID ? true : false}
            disabled={disableDepartmentController()}
            required
            defaultValue={editData ? editData?.CostCentreID : null}
          />

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              as={KeyboardDatePicker}
              name="InvoiceDate"
              required
              label="Invoice Date"
              control={control}
              format="dd/MM/yyyy"
              margin="dense"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{ 'aria-label': 'change date' }}
              showTodayButton
              className="left"
              value={new Date()}
              defaultValue={editData ? editData?.InvoiceDate : new Date()}
              helperText={errors?.InvoiceDate?.message}
              error={errors?.InvoiceDate ? true : false}
              onChange={(e: Date | null) => {}}
            />
          </MuiPickersUtilsProvider>

          <MuiPickersUtilsProvider utils={DateFnsUtils}>
            <Controller
              as={KeyboardDatePicker}
              name="TransactionDate"
              required
              label="Transaction Date"
              control={control}
              format="dd/MM/yyyy"
              margin="dense"
              allowKeyboardControl
              ref={register}
              KeyboardButtonProps={{ 'aria-label': 'change date' }}
              showTodayButton
              className="left"
              value={new Date()}
              defaultValue={editData ? editData?.TransactionDate : new Date()}
              helperText={errors?.TransactionDate?.message}
              error={errors?.TransactionDate ? true : false}
              onChange={(e: Date | null) => {}}
            />
          </MuiPickersUtilsProvider>

          <Controller
            name="Amount"
            control={control}
            defaultValue={editData?.Amount ?? 0}
            rules={{ required: true }}
            render={({ value }) => (
              <NumberFormat
                {...value}
                value={watch('Amount')}
                customInput={TextField}
                allowNegative={false}
                decimalScale={2}
                fixedDecimalScale
                thousandSeparator
                margin="normal"
                label="Billing Amount"
                id="standard-basic"
                required
                disabled={!isAdhocBilling}
                helperText={errors?.Amount?.message}
                error={errors?.Amount ? true : false}
                onValueChange={e => {
                  allowValueChange && handleAmtChange(e)
                  setAllowValueChange(true)
                }}
                defaultValue={editData?.Amount ?? 0}
              />
            )}
            helperText={errors?.Amount?.message}
            error={errors?.Amount ? true : false}
            disabled={!isAdhocBilling}
          />
          <Controller
            as={
              <TextField
                label="Tax Scheme"
                required
                fullWidth
                SelectProps={{
                  renderValue: () =>
                    `${selectedTax?.name} | ${selectedTax?.description} ${
                      selectedTax?.isExclusive ? `(E)` : `(I)`
                    }`,
                  onChange: event => {
                    handleTaxChange(event?.target?.value)
                    setValue('TaxSchemeID', event?.target?.value.toString())
                    clearErrors('TaxSchemeID')
                    setMountDefVal(false)
                  },
                }}
                select
                helperText={errors?.TaxSchemeID?.message}
                error={errors?.TaxSchemeID ? true : false}
              >
                <ListSubheader>Output</ListSubheader>
                {taxSchemeOutput?.map((taxScheme, index) => (
                  <MenuItem id="tax-select" key={index} value={taxScheme?.id}>
                    {
                      <div className="content-wrap full">
                        <div>
                          <span className="xsTitle">{taxScheme?.name}</span>
                        </div>
                        <div className="desc">
                          {taxScheme?.description}{' '}
                          {taxScheme?.isExclusive ? `(E)` : `(I)`}
                        </div>
                      </div>
                    }
                  </MenuItem>
                ))}
              </TextField>
            }
            select
            name="TaxSchemeID"
            autoComplete="off"
            control={control}
            defaultValue={editData?.TaxSchemeID || ''}
            ref={register}
          />

          {result.taxDetails.length > 0 && (
            <Grid item xs={12}>
              <Paper
                elevation={1}
                style={{
                  padding: '1px 7px',
                  borderRadius: '2px',
                  backgroundColor: '#fff9f2',
                }}
              >
                {result.taxDetails.map((tax, index) => (
                  <Grid container key={index} spacing={1} alignItems="center">
                    <Grid item xs={4}>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: '0.63rem',
                          fontWeight: 500,
                          lineHeight: '24px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {tax?.taxCode || `Tax ${index + 1}`}
                      </Typography>
                    </Grid>

                    <Grid item xs={3}>
                      <Typography
                        variant="body2"
                        style={{
                          fontSize: '0.63rem',
                          fontWeight: 500,
                          lineHeight: '24px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        {typeof tax?.TaxRate === 'number'
                          ? `${(tax.TaxRate * 100).toFixed(2)}%`
                          : `N/A`}
                      </Typography>
                    </Grid>

                    <Grid item xs={5}>
                      <Controller
                        name={`TaxAmtDetail[${index}]`}
                        control={control}
                        defaultValue={tax.TaxAmount}
                        render={({ onChange, value }) => (
                          <NumberFormat
                            customInput={TextField}
                            fullWidth
                            className="disabled-input"
                            thousandSeparator
                            decimalScale={2}
                            fixedDecimalScale
                            style={{
                              marginTop: '10px',
                              fontSize: '0.63rem',
                            }}
                            allowNegative={false}
                            size="small"
                            value={
                              watch(`TaxAmtDetail[${index}]`) ?? tax.TaxAmount
                            }
                            InputProps={{
                              disableUnderline: true,
                              readOnly: true,
                              style: {
                                fontSize: '0.63rem',
                                height: '23px',
                                padding: '0 4px',
                              },
                            }}
                            InputLabelProps={{ style: { display: 'none' } }}
                          />
                        )}
                      />
                    </Grid>
                  </Grid>
                ))}
              </Paper>
            </Grid>
          )}

          <Controller
            as={<NumberFormat allowNegative={false} />}
            thousandSeparator
            customInput={TextField}
            ref={register()}
            control={control}
            margin="dense"
            name="TaxAmt"
            label="Tax Amount"
            onValueChange={e => {
              setTaxAmt(e?.floatValue)
            }}
            value={taxAmt}
            defaultValue={editData?.TaxAmt ?? taxAmt ?? 0}
            decimalScale={2}
            fixedDecimalScale
            className="left"
            disabled
            InputProps={{ disableUnderline: true, readOnly: true }}
          />

          <Controller
            render={({ value, onChange }) => {
              value = { docAmt }
              return (
                <TextField
                  margin="dense"
                  label="Doc Amount"
                  value={
                    selectedTax?.isExclusive
                      ? amtStr(calculateDocAmt(billingAmt, taxAmt))
                      : amtStr(billingAmt)
                  }
                  InputProps={{ readOnly: true, disableUnderline: true }}
                  className="right"
                  disabled
                />
              )
            }}
            disabled
            margin="dense"
            defaultValue={(docAmt || 0) ?? (editData?.DocAmt || 0)}
            name="DocAmt"
            ref={register()}
            control={control}
          />

          <Controller
            as={TextField}
            id="standard-basic"
            name="RefNo"
            label="Reference No"
            defaultValue={editData ? editData?.RefNo : ''}
            autoComplete="off"
            control={control}
            fullWidth
            margin="normal"
            ref={register}
            required
            helperText={errors?.RefNo?.message}
            error={errors?.RefNo ? true : false}
          />
          <Controller
            as={TextField}
            id="standard-basic"
            name="Description"
            label="Description"
            defaultValue={editData ? editData?.Description : ''}
            autoComplete="off"
            control={control}
            fullWidth
            margin="normal"
            ref={register}
            required
            helperText={errors?.Description?.message}
            error={errors?.Description ? true : false}
          />
          <Controller
            as={TextField}
            id="standard-basic"
            name="Remark"
            label="Remark"
            defaultValue={editData ? editData?.Remark : ''}
            autoComplete="off"
            control={control}
            fullWidth
            margin="normal"
            ref={register}
            required
            helperText={errors?.Remark?.message}
            error={errors?.Remark ? true : false}
          />
          <div style={{ width: '100%', marginTop: '24px' }}>
            <FileUploadInput
              placeholder={previewFiles.length === 0 ? 'Attachment' : null}
              label={previewFiles.length > 0 ? 'Attachment' : null}
              name="Attachment"
              files={files}
              onHandleUploadChange={handleUploadChange}
              multiple
              accept={
                '.png, .jpg, .jpeg, .gif, .mp4, .avi, .mkv, .mov, .flv, .3gp, application/msword, application/pdf, application/vnd.ms-excel, application/*, application/vnd.openxmlformats-officedocument.wordprocessingml.document'
              }
              imagePreview={
                <>
                  {previewFiles?.map((v, i) => (
                    <UploadPreview
                      remove
                      key={v}
                      src={v}
                      onClick={() => removeFile(i)}
                      mediaType={
                        files[i]?.type ?? DocData?.DocumentListing[i]?.mediaType
                      }
                    />
                  ))}
                </>
              }
            />
          </div>
        </CardContents>
      </ContentWrapper>
      {footerOptions?.length > 0 && (
        <AccountFooter options={[...footerOptions]} />
      )}
      <TooltipProforma
        UnbilledAmt={selectedOrder?.unbilledAmount}
        Amount={watch('Amount') ?? 0}
      />
      <ErrorDialog
        errorDia={errDialog}
        setErrorDia={setErrDialog}
        errorMsg={errMessage}
        errorHeaderMsg={'Error!'}
      />
      <ExitConfirmationDialog
        openExitConf={openExitConf}
        setOpenExitConf={setOpenExitConf}
        onConfirm={() => {
          history.push(`/account0receivable/${CompanyID}/scheduled-billing`)
        }}
      />
    </>
  )
}
